import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface TagInputProps {
  mediaId: string;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export const TagInput = ({ mediaId, selectedTags, onTagsChange }: TagInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const queryClient = useQueryClient();

  const { data: allTags } = useQuery({
    queryKey: ['media-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_tags')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const addTagMutation = useMutation({
    mutationFn: async (tagName: string) => {
      // First, ensure the tag exists
      let tagId: string;
      const existingTag = allTags?.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      
      if (existingTag) {
        tagId = existingTag.id;
      } else {
        const { data, error } = await supabase
          .from('media_tags')
          .insert({ name: tagName })
          .select()
          .single();
        
        if (error) throw error;
        tagId = data.id;
        queryClient.invalidateQueries({ queryKey: ['media-tags'] });
      }

      // Then create the relation
      const { error: relationError } = await supabase
        .from('media_tag_relations')
        .insert({ media_id: mediaId, tag_id: tagId });
      
      if (relationError) throw relationError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      setInputValue("");
      setShowSuggestions(false);
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: async (tagName: string) => {
      const tag = allTags?.find(t => t.name === tagName);
      if (!tag) return;

      const { error } = await supabase
        .from('media_tag_relations')
        .delete()
        .eq('media_id', mediaId)
        .eq('tag_id', tag.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
    },
  });

  const suggestions = allTags
    ?.filter(tag => 
      tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedTags.includes(tag.name)
    )
    .slice(0, 5);

  const handleAddTag = (tagName: string) => {
    if (!tagName.trim() || selectedTags.includes(tagName.trim())) return;
    addTagMutation.mutate(tagName.trim());
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {selectedTags.map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1">
            {tag}
            <button
              onClick={() => removeTagMutation.mutate(tag)}
              className="ml-1 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <Input
            placeholder="Add tags..."
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(e.target.value.length > 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag(inputValue);
              }
            }}
          />
          <Button
            size="sm"
            onClick={() => handleAddTag(inputValue)}
            disabled={!inputValue.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {showSuggestions && suggestions && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md">
            {suggestions.map(tag => (
              <button
                key={tag.id}
                className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                onClick={() => handleAddTag(tag.name)}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
